import React, { useState, useEffect } from "react";
import { useDispatch } from 'react-redux'
import ProductInventory from "../../components/Product/productInventory";
import { ProductApi } from "../../api/productApi";
import { getSerialNumber } from "../../utils/sharedFunctions";
import { productVariantSorting } from "../../config/sortingLabels";
import useListingActions from '../../hooks/useListingActions'
import { Grid, Autocomplete, TextField, Button } from '@mui/material';
import { StoreApi } from '../../api/storeApi'
import { setSuccessNotification, setErrorNotification } from '../../actions/notificationAction'
import { BulkStockSyncModal, BulkNotifyWaitlistModal } from '../../shared-components';
import useTranslation from '../../hooks/useTranslation'
import StoreSelect from '../../components/Shared/StoreSelect';
import { ListingPage, ListTable, ListTableActions } from '../../shared-components'


const ProductInventoryContainer = () => {
  const [selectedStore, setSelectedStore] = useState(() => JSON.parse(localStorage.getItem('inventorySelectedStore')) || null);
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [waitlistVariants, setWaitlistVariants] = useState([]);
  const [syncOpen, setSyncOpen] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const handleStoreChange = (e, value) => {
    setSelectedStore(value)
    localStorage.setItem('inventorySelectedStore', JSON.stringify(value))
    fetchData({ page: 1, per_page: perPage, q: searchTerm, sort_by: sortOptionSelected, ...value && {store_id: value.id} }, true)
  }

  const handleExtraParameters = () => {
    if (selectedStore) {
      return { store_id: selectedStore.id }
    } else {
      return {}
    }
  }

  const {
    data,
    count,
    page,
    perPage,
    searchTerm,
    setData,
    sortOptionSelected,
    handlerForSearchingAndSortingChange,
    fetchData,
  } = useListingActions('product_inventory', ProductApi.getProductVariants, 'latest', handleExtraParameters );

  const handleNotifySelected = async (selectedVariantIds = []) => {
    try {
      if (selectedVariantIds.length === 0) return;
      await ProductApi.sendWaitlistEmails({ variant_ids: selectedVariantIds, store_id: selectedStore.id });
      dispatch(setSuccessNotification({ message: t('response_messages.waitlist_notify_success') }));
    } catch (err) {
      dispatch(setErrorNotification({ message: t('response_messages.waitlist_notify_failed') }));
    } finally {
      setNotifyModalOpen(false);
    }
  } 
  
  const handleOpenNotifyModal = async () => {
    try {
      const response = await ProductApi.getWaitlistVariants(selectedStore.id);
      if (response.status === 200) {
        setWaitlistVariants(response.data.variants);
        setNotifyModalOpen(true);
      }
    } catch (err) {
      dispatch(setErrorNotification({ message: t('response_messages.waitlist_fetch_failed') }));
    }
  }

  const handleSyncOpen = async () => {
    setSyncOpen(true);
    setSyncLoading(true);
    try {
      const response = await ProductApi.fetchBulkStock(selectedStore.id);
      if (response.status === 200) {
        setStockData(response.data.products);
      }
    } catch (err) {
      dispatch(setErrorNotification({ message: t('response_messages.bulk_stock_fetch_failed') }));
    } finally {
      setSyncLoading(false);
    }
  };
  
  const handleSyncConfirm = async (selectedSkus = []) => {
    try {
      await ProductApi.bulkSyncStock({ skus: selectedSkus, store_id: selectedStore.id });
      dispatch(setSuccessNotification({ message: t('response_messages.bulk_stock_sync_success') }));
    } catch (error) {
      dispatch(setErrorNotification({ message: t('response_messages.bulk_stock_sync_failed') }));
    }
    setSyncOpen(false);
  };

  const tableContent = productTableContent( data, page, perPage, selectedStore?.id)

  const tableActions = (
    <ListTableActions
      searchTerm={searchTerm}
      searchText='Search Products and Variants'
      sortOptionSelected={sortOptionSelected}
      getData={handlerForSearchingAndSortingChange}
      sortOptions={productVariantSorting}
    />
  )
  const buttons = (
    <Grid container alignItems="center" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
      <Grid item>
        <StoreSelect selectedStore={selectedStore} onChange={handleStoreChange} />
      </Grid>
      {selectedStore && (
        <>
          <Grid item>
            <Button variant="outlined" color="primary" onClick={handleOpenNotifyModal} sx={{ minWidth: 140, px: 2 }} >
               {t('labels.notifyUsers')}
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" color="primary" onClick={handleSyncOpen} sx={{ minWidth: 120, px: 2 }} >
              {t('labels.syncStock')}
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  )  

  return (
    <>
      <ListingPage
        title="Product Inventory"
        actions={buttons}
        table={
          <ListTable
            tableHeadings={tableContent.productInventoryHeading}
            tableContent={tableContent.productInventoryBody}
            tableActions={tableActions}
            count={count}
            page_no={page}
            rowsPerPage={perPage}
            onPageChange={(event, pageNo) => handlerForSearchingAndSortingChange('page', pageNo + 1)}
            handleChangeRowsPerPage={(event) => handlerForSearchingAndSortingChange('pageSize', event.target.value)}
            rowsControl={true} />
        } />
        <BulkNotifyWaitlistModal
          isOpen={notifyModalOpen}
          onClose={() => setNotifyModalOpen(false)}
          variantData={waitlistVariants}
          onNotify={handleNotifySelected}
        />
        <BulkStockSyncModal
          isOpen={syncOpen}
          onClose={() => setSyncOpen(false)}
          stockData={stockData}
          loading={syncLoading}
          onConfirm={handleSyncConfirm}
        />
    </>
  )
}

const productTableContent = ( productVariants=[], page_no, per_page, storeId) => {
  const productInventoryBody = productVariants.map( (productVariant,index) => (
    <ProductInventory key={index} productVariant={productVariant} index={getSerialNumber(page_no, per_page, index)} storeId={storeId}/>
  ))
  return { productInventoryBody: productInventoryBody, productInventoryHeading: ['Id', '', 'Name', 'SKU', 'Incoming', 'Available', 'Waitlist', ''] }
}

export default ProductInventoryContainer;